/**
 * CSV Preview Table Component
 *
 * Displays parsed CSV data with validation status per row.
 */

import { RowValidationResult } from '../../../utils/csvValidation';

interface CSVPreviewTableProps {
  validationResults: RowValidationResult[];
  onRemoveRow: (rowIndex: number) => void;
}

export function CSVPreviewTable({
  validationResults,
  onRemoveRow,
}: CSVPreviewTableProps) {
  const validCount = validationResults.filter((r) => r.isValid).length;
  const invalidCount = validationResults.length - validCount;

  return (
    <div>
      {/* Summary */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <span className="text-gray-600">
          {validationResults.length} row{validationResults.length !== 1 ? 's' : ''} found
        </span>
        {validCount > 0 && (
          <span className="text-green-600">
            {validCount} valid
          </span>
        )}
        {invalidCount > 0 && (
          <span className="text-red-600">
            {invalidCount} with errors
          </span>
        )}
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="max-h-80 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Venue
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Times
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">

                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {validationResults.map((result) => (
                <tr
                  key={result.rowIndex}
                  className={result.isValid ? '' : 'bg-red-50'}
                >
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {result.rowIndex + 1}
                  </td>
                  <td className="px-4 py-3">
                    {result.isValid ? (
                      <span className="text-green-600" title="Valid">
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    ) : (
                      <span
                        className="text-red-600 cursor-help"
                        title={result.errors.join('\n')}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {result.inputData.name || <span className="text-red-400 italic">Missing</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {result.inputData.venue || <span className="text-red-400 italic">Missing</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {result.inputData.eventDate || <span className="text-red-400 italic">Missing</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {[
                      result.inputData.callTime && `Call: ${result.inputData.callTime}`,
                      result.inputData.startTime && `Start: ${result.inputData.startTime}`,
                      result.inputData.endTime && `End: ${result.inputData.endTime}`,
                    ]
                      .filter(Boolean)
                      .join(', ') || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onRemoveRow(result.rowIndex)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                      title="Remove row"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error details for invalid rows */}
      {invalidCount > 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="text-sm font-medium text-red-800 mb-2">
            Validation Errors
          </h4>
          <ul className="text-sm text-red-700 space-y-1">
            {validationResults
              .filter((r) => !r.isValid)
              .map((r) => (
                <li key={r.rowIndex}>
                  <span className="font-medium">Row {r.rowIndex + 1}:</span>{' '}
                  {r.errors.join('; ')}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
